/* -------------------------------------------------------------------------- */
/*  Journey – Vista pipe-delimited TXT export (v3)                           */
/* -------------------------------------------------------------------------- *
 *  Record types:                                                             *
 *    6 = Estimate info (project name + address)                              *
 *    1 = Contract items (one per bid package, aggregated amount)             *
 *    2 = Phase records (MF3 → bid package mapping)                          *
 *    3 = Cost type records (one per line item / markup)                      *
 *  - Pipe-delimited, no quoting                                             *
 *  - Returns [stringLines, "txt"]                                           */
/* -------------------------------------------------------------------------- */

import { ValidationError } from "../../errors";
import { identifyCategory, CategoryMap } from "./categories";
import { BudgetImportConfig, BudgetRow } from "./framework/types";
import { assertDetailTotalMatches, truncateList } from "./framework/helpers";

/* ───────────────────────── 1. Constants ─────────────────────────────────── */

/** Cost type for all markups (GC Add-ons) */
const MARKUP_COST_TYPE = "8";

/** LMSEO column → Vista cost type mapping */
const LMSEO_COST_TYPE_MAP: CategoryMap = {
  labor: "1",
  material: "3",
  equip: "5",
  sub: "7",
  other: "4",
  total: "7", // fallback when all LMSEO components are 0
};

/** Row types that produce export records */
const EXPORTABLE_TYPES = new Set(["line_item", "markup", "atl_markup"]);

/* ───────────────────────── 2. Types ─────────────────────────────────────── */

/** Intermediate data for a Type 3 (cost detail) record */
interface Type3Record {
  mf3Code: string;
  costType: string;
  units: string;
  um: string;
  hours: string;
  amount: number;
  /** Bid package code — used to aggregate into Type 1 records */
  bpCode: string;
}

/* ───────────────────────── 3. Validation ─────────────────────────────────── */

/**
 * Validate all exportable rows have an MF3 cost code.
 * Throws ValidationError listing items that are missing codes.
 */
function validateMF3Codes(rows: BudgetRow[]): void {
  const missing: string[] = [];
  for (const row of rows) {
    if (!row["mf3_code"]) missing.push(row.name || "(unnamed item)");
  }
  if (missing.length > 0) {
    throw new ValidationError(
      `Missing MasterFormat cost code (MF3) for: ${truncateList(missing)}. Assign MF3 codes to all line items and markups before exporting.`
    );
  }
}

/**
 * Validate all exportable rows have a Bid Package code.
 * Throws ValidationError listing items missing bid packages.
 */
function validateBidPackages(rows: BudgetRow[]): void {
  const missing: string[] = [];
  for (const row of rows) {
    if (!row["Bid Package_code"]) missing.push(row.name || "(unnamed item)");
  }
  if (missing.length > 0) {
    throw new ValidationError(
      `Missing Bid Package for: ${truncateList(missing)}. Assign a Bid Package to all line items and markups before exporting.`
    );
  }
}

/**
 * Validate that each MF3 code maps to exactly one Bid Package.
 * Returns the MF3→BP mapping for reuse in Type 2 record generation.
 */
function validateMF3ToBPMapping(rows: BudgetRow[]): Map<string, string> {
  const mf3ToBP = new Map<string, string>();
  for (const row of rows) {
    const mf3 = (row["mf3_code"] as string).replace(/\s/g, "");
    const bp = row["Bid Package_code"] as string;
    const existing = mf3ToBP.get(mf3);
    if (existing && existing !== bp) {
      throw new ValidationError(
        `MF3 code "${mf3}" is assigned to Bid Packages "${existing}" and "${bp}". Each MF3 code must map to exactly one Bid Package.`
      );
    }
    mf3ToBP.set(mf3, bp);
  }
  return mf3ToBP;
}

/* ───────────────────────── 4. Record builders ────────────────────────────── */

/** Record Type 6: Estimate info (project name + address, mail and ship identical) */
function buildType6(report: Record<string, any>): string {
  const name = report.project_name || "";
  const addr = report.address || "";
  const city = report.city || "";
  const state = report.state || "";
  const zip = report.zip || "";
  return ["6", name, addr, city, state, zip, addr, city, state, zip].join("|");
}

/** Record Type 1: Contract item (one per bid package) */
function buildType1(bpCode: string, bpDesc: string, um: string, totalAmount: number): string {
  return ["1", bpCode, bpDesc, um, totalAmount.toFixed(2), bpDesc].join("|");
}

/** Record Type 2: Phase record (MF3 → bid package mapping) */
function buildType2(bpCode: string, mf3Code: string, mf3Desc: string): string {
  return ["2", bpCode, mf3Code, mf3Desc].join("|");
}

/** Record Type 3: Cost type record */
function buildType3(rec: Type3Record): string {
  return ["3", rec.mf3Code, rec.costType, rec.units, rec.um, rec.hours, rec.amount.toFixed(2)].join("|");
}

/* ───────────────────────── 5. Config ─────────────────────────────────────── */

export const journeyConfig: BudgetImportConfig = {
  format: "vista",
  requires: { projectReport: true },
  vista: {
    ext: "txt",
    render: (ctx) => {
      // buildType6 needs project-level fields (project_name/address/city/state/zip) which live on
      // ProjectReport, not EstimateReport — merge both so the Type-6 record isn't blank.
      const report = { ...ctx.projectReport, ...ctx.estimateReport };
      const estimateRows = ctx.rows;

      /* Filter to exportable rows */
      const rows = estimateRows.filter((r) => EXPORTABLE_TYPES.has(r.type));

      if (rows.length === 0) {
        throw new ValidationError(
          "No line items or markups found in the estimate. Load estimate data before exporting."
        );
      }

      /* Validate */
      validateMF3Codes(rows);
      validateBidPackages(rows);
      const mf3ToBP = validateMF3ToBPMapping(rows);

      /* Build Type 3 records (single pass — also aggregates for Types 1 & 2) */
      const type3Records: Type3Record[] = [];
      const bpAgg = new Map<string, { desc: string; total: number; um: string | null }>();
      const mf3Descs = new Map<string, string>();

      for (const row of rows) {
        const mf3Code = (row["mf3_code"] as string).replace(/\s/g, "");
        const bpCode = row["Bid Package_code"] as string;
        const bpDesc = (row["Bid Package_desc"] as string) || "";

        if (row.type === "markup" || row.type === "atl_markup") {
          const amount: number = Number(row.total) || 0;
          if (Math.abs(amount) < 0.005) continue;
          type3Records.push({
            mf3Code,
            costType: MARKUP_COST_TYPE,
            units: "1.00",
            um: "LS",
            hours: "",
            amount,
            bpCode,
          });
        } else {
          const quantity: number = row.quantity ?? 0;
          const customCostType = (row["Cost Type_code"] as string) || "";
          // Use original prod_rate (before ETL synthesis) to avoid reporting
          // hours when the user never entered a production rate
          const prodRate: number = row.prod_rate_original ?? row.prod_rate ?? 0;
          const laborHours = prodRate > 0 ? quantity * prodRate : 0;

          // Split line item into per-LMSEO-component records
          const components = identifyCategory(row, LMSEO_COST_TYPE_MAP);
          for (const [costType, componentUc] of components) {
            const amount = quantity * componentUc;
            if (Math.abs(amount) < 0.005) continue;

            // For sub/total categories, use custom sort code if assigned
            const resolvedCostType = costType === "7" && customCostType ? customCostType : costType;
            const isLabor = costType === "1";
            type3Records.push({
              mf3Code,
              costType: resolvedCostType,
              units: quantity.toFixed(2),
              um: row.uom || "",
              hours: isLabor && laborHours > 0 ? laborHours.toFixed(2) : "",
              amount,
              bpCode,
            });
          }
        }

        // Collect BP descriptions, UOMs, and MF3 descriptions (Type 1/2 metadata).
        // Actual Type 1 totals are aggregated from Type 3 records below to avoid
        // rounding divergence between row-level total_uc and sum(LMSEO components).
        const rowUm = row.type === "markup" || row.type === "atl_markup" ? "LS" : row.uom || "";
        const existing = bpAgg.get(bpCode);
        if (existing) {
          if (existing.um !== null && existing.um.toLowerCase() !== rowUm.toLowerCase()) {
            existing.um = null;
          }
        } else {
          bpAgg.set(bpCode, { desc: bpDesc, total: 0, um: rowUm });
        }

        // Collect MF3 descriptions for Type 2 (first occurrence wins)
        if (!mf3Descs.has(mf3Code)) {
          mf3Descs.set(mf3Code, (row["mf3_desc"] as string) || "");
        }
      }

      // Aggregate Type 1 totals from Type 3 records (bottom-up) so both sides
      // of the validation use the same source amounts.
      for (const rec of type3Records) {
        const bp = bpAgg.get(rec.bpCode);
        if (bp) bp.total += rec.amount;
      }

      /* Validate grand totals */
      assertDetailTotalMatches(
        rows,
        type3Records.reduce((sum, r) => sum + r.amount, 0)
      );

      /* Assemble output */
      const type6Line = buildType6(report);

      const type1Lines = Array.from(bpAgg.entries())
        .filter(([, { total }]) => total !== 0)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([code, { desc, total, um }]) => buildType1(code, desc, um ?? "ls", total));

      const type2Lines = Array.from(mf3Descs.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([mf3Code, mf3Desc]) => buildType2(mf3ToBP.get(mf3Code)!, mf3Code, mf3Desc));

      const type3Lines = type3Records.map(buildType3);

      return [type6Line, ...type1Lines, ...type2Lines, ...type3Lines];
    },
  },
};
